import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../../utils/authStorage';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import { generateYearRange } from '../../../../../Custom/Custom';
import asiTitle from '../../../../../asi.json';

// API configuration
const getAuthConfig = () => {
  if (!hasAuthSession()) return null;
  return { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Cache-Control': 'no-cache' } };
};

const BlockGReport = () => {
  const navigate = useNavigate();

  // State for report data
  const [itemData, setItemData] = useState({});
  const [blockGData, setBlockGData] = useState([]);
  const [blockCDFData, setBlockCDFData] = useState({
    blockCList: [],
    blockDList: [],
    blockFList: [],
    diffValueFormD: 0,
    diffValue: 0,
  });
  const [loading, setLoading] = useState(true);

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) return {};

    const parsedData = JSON.parse(balanceSheetData);
    return {
      dslNo: parsedData.dslNo || '',
      surveyYear: parsedData.year || '2022-23',
      nameUnit: parsedData.nameUnit || '',
      pslNo: parsedData.pslNo || ''
    };
  }, []);

  const storedLanguage = localStorage.getItem('language') || 'en';

  // Memoized data transformation
  const transformBlockGData = useCallback((data) => {
    const initialData = {};
    data.forEach((item, index) => {
      initialData[item.itemCode] = {
        dslNo: item.dslNo,
        surveyYear: item.surveyYear,
        itemCode: item.itemCode,
        recipts: item.recipts,
        statusFlag: item.statusFlag,
        ItemsName: item.ItemsName,
        reciptsClass: 'textBox_back_color_white'
      };
    });
    return initialData;
  }, []);

  // Fetch data function
  const fetchBlockGData = useCallback(async () => {
    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year');
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0]
    };

    showLoading(true);

    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return;
      }

      const response = await axios.post(`${API_URL}api/asi/blockGForm`, requestPayload, config);
      const { data, status } = response.data;

      if (status && data?.blockGData?.length > 0) {
        setBlockGData(data.blockGData);
        setBlockCDFData({
          blockCList: data.blockCList || [],
          blockDList: data.blockDList || [],
          blockFList: data.blockFList || [],
          diffValueFormD: data.diffValueFormD || 0,
          diffValue: data.diffValue || 0,
        });
        const transformedData = transformBlockGData(data.blockGData);
        setItemData(transformedData);
        console.log('Block G data loaded successfully:', data.blockGData.length, 'items');
      } else {
        setBlockGData([]);
        toast.warning('No Block G data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      }
    } catch (error) {
      console.error('API Error:', error);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      } else {
        toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
      }
      setBlockGData([]);
    } finally {
      showLoading(false);
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear, transformBlockGData]);

  useEffect(() => {
    fetchBlockGData();
  }, [fetchBlockGData]);

  // Memoized table headers
  const tableHeaders = useMemo(() => {
    const isHindi = storedLanguage === 'hi';
    return {
      slNo: isHindi ? asiTitle.hi.SL_NO : asiTitle.en.SL_NO,
      items: isHindi ? asiTitle.hi.ITEMS : asiTitle.en.ITEMS,
      receipts: isHindi ? asiTitle.hi.RECEIPTS : asiTitle.en.RECEIPTS,
      dslNo: isHindi ? asiTitle.hi.DSL_NO : asiTitle.en.DSL_NO,
      surveyYear: isHindi ? asiTitle.hi.SERVEY_YEAR : asiTitle.en.SERVEY_YEAR,
      blockGTitle: isHindi ? asiTitle.hi.BLOCK_G_TITLE : asiTitle.en.BLOCK_G_TITLE
    };
  }, [storedLanguage]);

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      <style>
        {`
          .report-container {
            background-color: #fff;
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 20px;
          }
          .report-title {
            background-color: #fff;
            color: #333;
            font-size: 18px;
            font-weight: bold;
            padding: 15px 20px;
            margin: -20px -20px 20px -20px;
            border-radius: 6px 6px 0 0;
            border-bottom: 2px solid #333;
            text-align: center;
          }
          .report-info {
            background-color: #f8f9fa;
            padding: 10px 15px;
            margin-bottom: 20px;
            border-radius: 4px;
            border-left: 4px solid #333;
          }
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 12px;
          }
          .report-table th,
          .report-table td {
            border: 1px solid #333;
            padding: 6px 4px;
            text-align: center;
            vertical-align: middle;
          }
          .report-table th {
            background-color: #f5f5f5;
            color: #333;
            font-weight: bold;
            font-size: 11px;
          }
          .report-table td {
            background-color: #fff;
          }
          .report-table .item-name {
            text-align: left;
            font-weight: 500;
            padding-left: 8px;
          }
          .report-table .bold-text {
            font-weight: bold;
          }
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 40px;
            background-color: #f8f9fa;
            border-radius: 4px;
          }
     
          @media print {
            .no-print { display: none !important; }
            .report-container { border: none; margin: 0; padding: 0; }
            .report-title { margin: 0; border-radius: 0; }
            .report-info { margin: 0 0 20px 0; }
            .btn_orange { display: none !important; }
          }
        `}
      </style>

      <div className="container-fluid py-3">
        {/* Report Content */}
        <div className="report-container">
          <div className="report-title">
            <div dangerouslySetInnerHTML={{ __html: tableHeaders.blockGTitle }} />
          </div>

          <div className="report-info">
            <div className="row">
              <div className="col-md-6">
                <strong>{tableHeaders.dslNo}:</strong> {blockInfo.dslNo || 'Not Available'}
              </div>
              <div className="col-md-6">
                <strong>{tableHeaders.surveyYear}:</strong> {blockInfo.surveyYear || 'Not Available'}
              </div>
            </div>
          </div>

          {blockGData.length > 0 ? (
            <div className="table-responsive">
              <table className="report-table">
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>{tableHeaders.slNo}</th>
                    <th style={{ width: '60%' }}>{tableHeaders.items}</th>
                    <th style={{ width: '30%' }}>{tableHeaders.receipts}</th>
                  </tr>
                  <tr>
                    <th>1</th>
                    <th>2</th>
                    <th>3</th>
                  </tr>
                </thead>
                <tbody>
                  {blockGData.map((item, index) => {
                    const isBold = index === 6; // Row 7 is bold in BlockG (index 6)

                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td className={`item-name ${isBold ? "bold-text" : ""}`}>
                          {item.ItemsName}
                        </td>
                        <td>{itemData[item.itemCode]?.recipts === "0" ? "" : itemData[item.itemCode]?.recipts || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">No Block G data available for this DSL No.</div>
          )}
        </div>

        {/* Action Buttons */}
        {/* <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print  
              <i className="fa fa-print ms-1"></i>
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/reports')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div> */}


        <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/report-selection')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/blockHreport')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlockGReport;